/**
 * Motion to Withdraw as Attorney of Record, and its Order (Kelsi, 19 Sep; Lloyd, 22 Sep).
 *
 * Same split as the continuance: `readiness` asks what is missing WITHOUT producing anything, so
 * blockers appear as staff fill the form, and `generate` always produces both documents together.
 *
 * 🔴 One deliberate difference. The grounds are NOT duplicated here. `options()` fetches them from
 * the server, because they are a form schema — eleven grounds, each with its own typed,
 * conditionally required fields — and Lloyd edits the wording himself. A hand-mirrored copy would
 * be wrong the first time he changes a sentence. Only DTO shapes are written out below, and those
 * carry no legal text.
 */

export interface GroundFieldDto {
  id: string;
  label: string;
  type: 'date' | 'number' | 'text';
  required: boolean;
  default?: string;
}

export interface GroundDefinitionDto {
  id: string;
  sort_order: number;
  label: string;
  mandatory_withdrawal: boolean;
  is_substitution?: boolean;
  suggest_consent: string;
  fields: GroundFieldDto[];
  usage_note?: string;
  text: string;
}

export interface LabelledTextDto {
  id: string;
  label: string;
  text: string;
}

export interface WithdrawalOptionsDto {
  grounds: GroundDefinitionDto[];
  consent_options: LabelledTextDto[];
  conference_options: LabelledTextDto[];
  setting_types: string[];
}

export interface WithdrawalBlockerDto {
  field: string | null;
  reason: string;
}

export interface WithdrawalReadinessDto {
  canGenerate: boolean;
  blockers: WithdrawalBlockerDto[];
  /** Worth saying before filing, but never a reason to stop. */
  warnings: WithdrawalBlockerDto[];
}

/** A setting still ahead of the case, as the server found it. */
export interface UpcomingSettingDto {
  courtEventGuid: string;
  startMs: number;
  date: string;
  title: string;
  isTrialSetting: boolean;
  /** Only set where the record actually says what kind of setting it is. */
  suggestedType: string | null;
}

export interface WithdrawalReadinessResponse {
  readiness: WithdrawalReadinessDto;
  settings: UpcomingSettingDto[];
  clientAddress: string;
  prosecutorName: string;
}

export interface SelectedGroundDto {
  id: string;
  fields?: Record<string, string>;
}

export interface ConfirmedSettingDto {
  type: string;
  date: string;
  time?: string;
}

export interface GenerateWithdrawalMotionInput {
  grounds: SelectedGroundDto[];
  consent?: string | null;
  settings: ConfirmedSettingDto[];
  noSettingsKnown?: boolean;
  requestReset?: boolean;
  resetSettingDescription?: string | null;
  conference?: { status: string; date?: string; prosecutorName?: string } | null;
  clientServiceMethod?: string | null;
  showClientApproval?: boolean;
  /** Save a staff-only copy on the case. Off by default: generating is not filing. */
  fileOnCase?: boolean;
  servedAt?: string;
  servedMethod: string;
  servedOn: string;
  signatureDate?: string;
  courtName?: string | null;
}

export interface WithdrawalDocumentDto {
  fileName: string;
  pdfBase64: string;
}

export interface WithdrawalMotionResultDto {
  motion: WithdrawalDocumentDto;
  order: WithdrawalDocumentDto;
  warnings: WithdrawalBlockerDto[];
  /** True when a copy was stored on the case, which only happens when asked for. */
  filedOnCase: boolean;
}

/** Thrown on a 422, so the panel can show the blockers rather than a generic failure. */
export class WithdrawalMotionBlockedError extends Error {
  public readonly blockers: WithdrawalBlockerDto[];

  constructor(message: string, blockers: WithdrawalBlockerDto[]) {
    super(message);
    this.name = 'WithdrawalMotionBlockedError';
    this.blockers = blockers;
  }
}

class WithdrawalMotionApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  /** Lloyd's grounds library. Fetched once per panel mount. */
  static async options(): Promise<WithdrawalOptionsDto> {
    const response = await fetch(`${WithdrawalMotionApi.host()}/api/v1/manager/withdrawal-motion/options`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Could not load the grounds for withdrawal.');
    return response.json();
  }

  static async readiness(
    ticketGuid: string,
    params: Record<string, string | undefined>,
  ): Promise<WithdrawalReadinessResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value);
    }
    const response = await fetch(
      `${WithdrawalMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/withdrawal-motion/readiness?${query.toString()}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
    );
    if (!response.ok) throw new Error('Could not check what the motion still needs.');
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateWithdrawalMotionInput,
  ): Promise<WithdrawalMotionResultDto> {
    const response = await fetch(
      `${WithdrawalMotionApi.host()}/api/v1/manager/ticket/${ticketGuid}/withdrawal-motion`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 422) {
      const body = await response.json().catch(() => ({ blockers: [] }));
      throw new WithdrawalMotionBlockedError(
        body.message || 'This case is not ready for a motion to withdraw.',
        body.blockers ?? [],
      );
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.details || error.message || error.error || 'Failed to generate the motion to withdraw.',
      );
    }
    return response.json();
  }
}

export default WithdrawalMotionApi;
